
import { FormControl, FormControlLabelProps, FormControlProps, FormLabel, RadioGroup, RadioProps, SxProps, Theme } from '@mui/material';
import { FC, useCallback } from 'react';

import { InputRadioGroupProps } from './types/FieldTypes';

export interface IRadioGroupProps extends InputRadioGroupProps {
    FormControlProps?: FormControlProps;
    RadioProps?: RadioProps;
    FormControlLabelProps?: Omit<FormControlLabelProps, 'label' | 'value' | 'control'>;
    label?: React.ReactNode;
    margin?: FormControlProps['margin'];
    fullWidth?: FormControlProps['fullWidth'];
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, eventValue: any | null | undefined) => void;
    optionLabelPlacement?: 'end' | 'start' | 'top' | 'bottom';
    optionLabelSx: SxProps<Theme>;
}

const InputRadioGroupField: FC<IRadioGroupProps> = ({
    FieldInputProps: { state: { value, propertyPath }, actions: { update, blur, focus, touch } },
    onChange,
    sx,
    onBlur: inputPropsBlur,
    onFocus: inputPropsFocus,
    RadioProps,
    FormControlProps,
    FormControlLabelProps,
    label,
    margin,
    fullWidth,
    optionLabelPlacement,
    optionLabelSx,
    children,
    ...rest
}) => {

    const hasChildren = !!children;

    const onChangeCombined = useCallback((event: React.ChangeEvent<HTMLInputElement>, eventvalue: any) => {
        if (onChange) {
            onChange(event, eventvalue);
        }
        
        const newValue = eventvalue !== undefined ?
            eventvalue :
            undefined as any

        update(
            newValue,
            true, true, true);
    }, [onChange, update, hasChildren]);

    const onFocusCombined = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    const onBlurCombined = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);
    
    const fullWidthToUse = fullWidth !== undefined ? 
        fullWidth :
        FormControlProps?.fullWidth !== undefined ?
            FormControlProps?.fullWidth :
            true;
    
    const marginToUse = margin !== undefined ? 
        margin :
        FormControlProps?.margin !== undefined ?
            FormControlProps?.margin :
            undefined;

    return (
        <FormControl
            {...FormControlProps}
            margin={marginToUse}
            error={FormControlProps?.error}
            fullWidth={fullWidthToUse}
            sx={{...FormControlProps?.sx}}
            {...rest as any}
        >
            {label && <FormLabel>{label}</FormLabel>}
            <RadioGroup
                {...rest as any}
                name={propertyPath}
                value={value?.toString() ?? null}
                onChange={onChangeCombined}
                onFocus={onFocusCombined}
                onBlur={onBlurCombined}
            >
                {children}
            </RadioGroup>
        </FormControl>

    );
}

export default InputRadioGroupField